import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";

export const SCHEMES = {
  forge: { base: "#181A1F", highlight: "#2A2E35", shadow: "#0F1114", isLight: false },
  classic: { base: "#E4E5E6", highlight: "#EDEEF0", shadow: "#D0D1D2", isLight: true },
  obsidian: { base: "#0A0A0E", highlight: "#14141A", shadow: "#050508", isLight: false },
  chalk: { base: "#2C2F36", highlight: "#3A3D44", shadow: "#1E2026", isLight: false },
  ivory: { base: "#F3EDE1", highlight: "#FAF6EE", shadow: "#DDD7CB", isLight: true },
};

const FACE_DEFS = [
  { dx: 0, dy: 0, dz: 1, rx: 0, ry: 0, rz: 0 },
  { dx: 0, dy: 1, dz: 0, rx: -Math.PI / 2, ry: 0, rz: 0 },
  { dx: 1, dy: 0, dz: 0, rx: 0, ry: Math.PI / 2, rz: 0 },
  { dx: -1, dy: 0, dz: 0, rx: 0, ry: -Math.PI / 2, rz: 0 },
  { dx: 0, dy: -1, dz: 0, rx: Math.PI / 2, ry: 0, rz: 0 },
  { dx: 0, dy: 0, dz: -1, rx: 0, ry: Math.PI, rz: 0 },
];

const RESULT_ROTATIONS = [
  { x: 0, y: 0 },
  { x: Math.PI / 2, y: 0 },
  { x: 0, y: -Math.PI / 2 },
  { x: 0, y: Math.PI / 2 },
  { x: -Math.PI / 2, y: 0 },
  { x: 0, y: Math.PI },
];

const FACE_SIDE = 0.8;
const FACE_RADIUS = 0.25;
const FACE_OFFSET = 0.5 * 0.94;
const EDGE_RADIUS = 0.2;
const FONT_STACK =
  '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';

function roundedBoxGeometry() {
  const geo = new THREE.BoxGeometry(1, 1, 1, 20, 20, 20);
  const pos = geo.attributes.position;
  const inner = 0.5 - EDGE_RADIUS;
  const v = new THREE.Vector3();
  const clamped = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    clamped.set(
      THREE.MathUtils.clamp(v.x, -inner, inner),
      THREE.MathUtils.clamp(v.y, -inner, inner),
      THREE.MathUtils.clamp(v.z, -inner, inner)
    );
    const dir = v.clone().sub(clamped);
    if (dir.lengthSq() > 1e-8) {
      dir.normalize().multiplyScalar(EDGE_RADIUS).add(clamped);
      pos.setXYZ(i, dir.x, dir.y, dir.z);
    }
  }
  const merged = mergeVertices(geo, 1e-4);
  merged.computeVertexNormals();
  return merged;
}

function roundedPlateGeometry() {
  const half = FACE_SIDE / 2;
  const r = Math.min(FACE_RADIUS, half);
  const shape = new THREE.Shape();
  shape.moveTo(-half + r, -half);
  shape.lineTo(half - r, -half);
  shape.absarc(half - r, -half + r, r, -Math.PI / 2, 0);
  shape.lineTo(half, half - r);
  shape.absarc(half - r, half - r, r, 0, Math.PI / 2);
  shape.lineTo(-half + r, half);
  shape.absarc(-half + r, half - r, r, Math.PI / 2, Math.PI);
  shape.lineTo(-half, -half + r);
  shape.absarc(-half + r, -half + r, r, Math.PI, 1.5 * Math.PI);

  const geo = new THREE.ShapeGeometry(shape, 24);
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, (uv.getX(i) + half) / FACE_SIDE, (uv.getY(i) + half) / FACE_SIDE);
  }
  uv.needsUpdate = true;
  return geo;
}

function wrapLines(ctx, word, size, maxWidth) {
  ctx.font = `900 ${size}px ${FONT_STACK}`;
  const words = word.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width <= maxWidth || !line) line = next;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function fits(ctx, word, size) {
  const maxWidth = 512 * 0.88;
  const maxHeight = 512 * 0.82;
  const lines = wrapLines(ctx, word, size, maxWidth);
  const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
  return widest <= maxWidth && lines.length * size * 1.12 <= maxHeight;
}

function bestFontSize(words) {
  const ctx = document.createElement("canvas").getContext("2d");
  let global = 180;
  for (const word of words) {
    let lo = 30;
    let hi = 180;
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (fits(ctx, word, mid)) lo = mid;
      else hi = mid;
    }
    global = Math.min(global, lo);
  }
  return global;
}

function faceTexture(word, scheme, accent, fontSize) {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  const light = scheme.isLight;

  ctx.fillStyle = scheme.base;
  ctx.fillRect(0, 0, size, size);

  const diagonal = ctx.createLinearGradient(0, 0, size, size);
  diagonal.addColorStop(0, `rgba(255,255,255,${light ? 0.06 : 0.1})`);
  diagonal.addColorStop(0.5, "rgba(255,255,255,0)");
  diagonal.addColorStop(1, `rgba(0,0,0,${light ? 0.08 : 0.2})`);
  ctx.fillStyle = diagonal;
  ctx.fillRect(0, 0, size, size);

  const sheen = ctx.createRadialGradient(140, 120, 10, 140, 120, 300);
  sheen.addColorStop(0, `rgba(255,255,255,${light ? 0.05 : 0.09})`);
  sheen.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, size, size);

  const shade = ctx.createRadialGradient(400, 420, 10, 400, 420, 290);
  shade.addColorStop(0, `rgba(0,0,0,${light ? 0.08 : 0.2})`);
  shade.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, size, size);

  const lines = wrapLines(ctx, word.toUpperCase(), fontSize, size * 0.88);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 ${fontSize}px ${FONT_STACK}`;
  const lineHeight = fontSize * 1.12;
  const startY = size / 2 - ((lines.length - 1) * lineHeight) / 2;

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.55)";
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 6;
  ctx.fillStyle = accent;
  lines.forEach((line, i) => ctx.fillText(line, size / 2, startY + i * lineHeight));
  ctx.restore();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

function glowTexture(accent) {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  const grad = ctx.createRadialGradient(size / 2, size / 2, 8, size / 2, size / 2, size / 2);
  grad.addColorStop(0, accent);
  grad.addColorStop(0.45, `${accent}66`);
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function luminance(hex) {
  const c = new THREE.Color(hex);
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

function layoutPositions(count, scale, spread = 1) {
  const sx = (count === 6 ? 1.1 : 0.95) * scale * spread;
  const sy = (count === 6 ? 1.45 : 1.25) * scale * spread;
  const patterns = {
    1: [[0, 0]],
    2: [[-sx, sy / 2], [sx, -sy / 2]],
    3: [[-sx, sy / 2], [0, 0], [sx, -sy / 2]],
    4: [[-sx, sy / 2], [sx, sy / 2], [-sx, -sy / 2], [sx, -sy / 2]],
    5: [[-sx, sy / 2], [sx, sy / 2], [0, 0], [-sx, -sy / 2], [sx, -sy / 2]],
    6: [[-sx, sy / 2], [sx, sy / 2], [-sx, 0], [sx, 0], [-sx, -sy / 2], [sx, -sy / 2]],
  };
  return patterns[Math.min(Math.max(count, 1), 6)];
}

function Die({ die, position, scheme, scale, fontSize, glowEnabled, glowStrength, rollKey, duration, index }) {
  const group = useRef();
  const glow = useRef();
  const shellGeo = useMemo(roundedBoxGeometry, []);
  const plateGeo = useMemo(roundedPlateGeometry, []);

  const shellMaterial = useMemo(
    () => new THREE.MeshStandardMaterial({ color: scheme.base, roughness: 0.5, metalness: 0.2 }),
    [scheme]
  );

  const faceMaterials = useMemo(
    () =>
      die.faces.map(
        (word) =>
          new THREE.MeshStandardMaterial({
            map: faceTexture(word, scheme, die.accentColor, fontSize),
            roughness: 0.85,
            metalness: 0.02,
            emissive: new THREE.Color(scheme.base),
            emissiveIntensity: 0.03,
            depthTest: false,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1,
            transparent: true,
          })
      ),
    [die, scheme, fontSize]
  );

  const glowMaterial = useMemo(() => {
    const intensity = THREE.MathUtils.clamp(1.05 - luminance(die.accentColor), 0.35, 1);
    return new THREE.MeshBasicMaterial({
      map: glowTexture(die.accentColor),
      transparent: true,
      opacity: intensity * (scheme.isLight ? 0.5 : 0.75) * glowStrength,
      blending: scheme.isLight ? THREE.NormalBlending : THREE.AdditiveBlending,
      depthWrite: false,
    });
  }, [die.accentColor, scheme, glowStrength]);

  const anim = useRef({ start: -1, fromX: 0, fromY: 0, toX: 0, toY: 0, lastKey: -1 });

  useFrame(({ clock }) => {
    const g = group.current;
    if (!g) return;
    const t = clock.getElapsedTime();
    const a = anim.current;

    if (a.lastKey !== rollKey) {
      a.lastKey = rollKey;
      const target = RESULT_ROTATIONS[(die.resultIndex ?? 0) % 6];
      a.fromX = g.rotation.x;
      a.fromY = g.rotation.y;
      a.toX = target.x + Math.PI * 2 * (5 + Math.floor(Math.random() * 2));
      a.toY = target.y + Math.PI * 2 * (3 + Math.floor(Math.random() * 2));
      a.start = t + index * 0.04;
    }

    const elapsed = t - a.start;
    const rolling = a.start >= 0 && elapsed >= 0 && elapsed < duration;

    if (rolling) {
      const p = elapsed / duration;
      const e = 1 - Math.pow(1 - p, 4);
      g.rotation.x = THREE.MathUtils.lerp(a.fromX, a.toX, e);
      g.rotation.y = THREE.MathUtils.lerp(a.fromY, a.toY, e);
      g.rotation.z = 0;
      if (glow.current) {
        const pulse = Math.exp(-4 * p);
        glow.current.scale.setScalar(1 + 0.32 * pulse);
        glow.current.material.opacity = glowMaterial.opacity * (1 + 0.9 * pulse);
      }
    } else {
      if (a.start >= 0 && elapsed >= duration) {
        const target = RESULT_ROTATIONS[(die.resultIndex ?? 0) % 6];
        a.fromX = target.x;
        a.fromY = target.y;
        a.start = -1;
        g.rotation.x = target.x;
        g.rotation.y = target.y;
      }
      const phase = t / 2.4 * Math.PI * 2 + index * 0.4;
      const amp = THREE.MathUtils.degToRad(4);
      g.rotation.x = a.fromX + Math.sin(phase) * amp;
      g.rotation.y = a.fromY + Math.cos(phase * 0.85) * amp;
      g.rotation.z = Math.sin(phase * 0.5) * amp * 0.12;
      if (glow.current) {
        glow.current.scale.setScalar(1);
        glow.current.material.opacity = glowMaterial.opacity;
      }
    }
  });

  const glowSize = (scheme.isLight ? 1.28 : 1.6) * scale;

  return (
    <group position={[position[0], position[1], 0]}>
      {glowEnabled && (
        <mesh ref={glow} position={[0, 0, -0.55]} material={glowMaterial}>
          <planeGeometry args={[glowSize, glowSize]} />
        </mesh>
      )}
      <group ref={group} scale={scale}>
        <mesh geometry={shellGeo} material={shellMaterial} renderOrder={0} />
        {FACE_DEFS.map((f, i) => (
          <mesh
            key={i}
            geometry={plateGeo}
            material={faceMaterials[i]}
            renderOrder={1}
            position={[f.dx * FACE_OFFSET, f.dy * FACE_OFFSET, f.dz * FACE_OFFSET]}
            rotation={[f.rx, f.ry, f.rz]}
          />
        ))}
      </group>
    </group>
  );
}

function FitCamera({ positions, scale }) {
  const { camera, size } = useThree();
  useEffect(() => {
    const halfDie = 0.5 * scale;
    const extentX = Math.max(...positions.map((p) => Math.abs(p[0]) + halfDie)) * 2;
    const extentY = Math.max(...positions.map((p) => Math.abs(p[1]) + halfDie)) * 2;
    const aspect = size.width / size.height;
    let halfW = (extentX * 1.2) / 2;
    let halfH = (extentY * 1.2) / 2;
    if (halfW / halfH < aspect) halfW = halfH * aspect;
    else halfH = halfW / aspect;
    camera.left = -halfW;
    camera.right = halfW;
    camera.top = halfH;
    camera.bottom = -halfH;
    camera.position.set(0, 0, 5);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }, [camera, size, positions, scale]);
  return null;
}

const ForgeDice = forwardRef(function ForgeDice(
  {
    dice,
    scheme = "forge",
    scale = 1,
    glowEnabled = true,
    glowStrength = 1,
    spread = 1,
    animationDuration = 1.6,
    className = "",
    testId = "forge-dice",
  },
  ref
) {
  const [rollKey, setRollKey] = useState(0);
  const activeScheme = SCHEMES[scheme] || SCHEMES.forge;
  const positions = useMemo(() => layoutPositions(dice.length, scale, spread), [dice.length, scale, spread]);
  const fontSize = useMemo(() => bestFontSize(dice.flatMap((d) => d.faces)), [dice]);

  useImperativeHandle(ref, () => ({ roll: () => setRollKey((k) => k + 1) }), []);

  return (
    <div className={`relative w-full h-full ${className}`} data-testid={testId}>
      <Canvas
        orthographic
        camera={{ position: [0, 0, 5], near: 0.1, far: 100 }}
        dpr={[1, 2]}
        gl={{ alpha: true, antialias: true }}
        onCreated={({ gl }) => {
          gl.outputColorSpace = THREE.SRGBColorSpace;
          gl.setClearAlpha(0);
        }}
      >
        <ambientLight intensity={0.39} />
        <directionalLight position={[3, 4, 5]} intensity={0.6} />
        <directionalLight position={[-3, 2, 3]} intensity={0.25} />
        <FitCamera positions={positions} scale={scale} />
        {dice.map((die, i) => (
          <Die
            key={i}
            index={i}
            die={die}
            position={positions[i]}
            scheme={activeScheme}
            scale={scale}
            fontSize={fontSize}
            glowEnabled={glowEnabled}
            glowStrength={glowStrength}
            rollKey={rollKey}
            duration={animationDuration}
          />
        ))}
      </Canvas>
    </div>
  );
});

export default ForgeDice;
